import { Alert, AlertIcon, Box, Text, type AlertProps } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { SourcingEventDto, SourcingEventPatchDto } from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { useRunsPublicProcurements } from "../../../../../common/organization/useRunsPublicProcurements";
import { useProcurementTerms } from "../../../../../common/terminology/useProcurementTerms";
import {
  formatThresholdNok,
  impliesSimplifiedFlow,
  partRunsInSimplifiedFlow,
  simplifiedFlowThresholdNok,
} from "../utils/foaPartDisplay";
import { ChangeRegimeButton } from "./ChangeRegimeButton";

export const useValueDisagreesWithRegime = (eventId: string): boolean => {
  const runsPublicProcurements = useRunsPublicProcurements();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { state, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  const isClassified = state.eventContext.type !== "Unclassified";
  const isPrivate = state.eventContext.type === "Private";
  const foaPart = publicContext?.foaPart ?? null;
  const estimatedValueNok =
    state.eventContext.estimatedValue?.value != null ? Number(state.eventContext.estimatedValue.value) : null;
  const governingRegulation = publicContext?.regulation ?? selectedOrg.defaultRegulation ?? null;
  const belongsInSimplifiedFlow =
    !runsPublicProcurements ||
    (foaPart == null
      ? impliesSimplifiedFlow(governingRegulation, estimatedValueNok)
      : partRunsInSimplifiedFlow(foaPart));
  const valueSettled = !["saving", "pending"].includes(fieldStatus("eventContext.estimatedValue"));
  return isClassified && valueSettled && estimatedValueNok != null && belongsInSimplifiedFlow !== isPrivate;
};

export const RegimeMismatchWarning = ({ eventId, ...alertProps }: { eventId: string } & AlertProps) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { state } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const disagrees = useValueDisagreesWithRegime(eventId);
  if (!disagrees) return null;

  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  const isPrivate = state.eventContext.type === "Private";
  const estimatedValueNok =
    state.eventContext.estimatedValue?.value != null ? Number(state.eventContext.estimatedValue.value) : null;
  const governingRegulation = publicContext?.regulation ?? selectedOrg.defaultRegulation ?? null;
  const simplifiedFlowThreshold = formatThresholdNok(simplifiedFlowThresholdNok(governingRegulation));

  return (
    <Alert status="warning" borderRadius="md" fontSize="sm" {...alertProps}>
      <AlertIcon />
      <Box minW="0">
        {isPrivate
          ? terms.valueAboveSimplifiedThreshold(simplifiedFlowThreshold)
          : impliesSimplifiedFlow(governingRegulation, estimatedValueNok)
            ? t("The estimated value is below {{threshold}} NOK, but this procurement is already registered as public", {
                threshold: simplifiedFlowThreshold,
              })
            : t("This is a Del I procurement with no announcement duty, but it is registered as public")}
        <Text fontSize="xs" color="smMuted" mt="1">
          {t("The classification is not changed automatically — changing it discards the work done under the current one")}
        </Text>
        <ChangeRegimeButton eventId={eventId} currentRegime={isPrivate ? "Private" : "Public"} />
      </Box>
    </Alert>
  );
};
